import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    ArrowRight,
    Zap,
    Cable,
    ShieldAlert,
    Droplets,
    Clock,
    Wrench,
    Radio,
    Activity,
    Layers3,
    Cpu,
    CheckCircle2,
    Sparkles,
    Building2,
    Truck,
    Hotel,
    Warehouse,
    Anchor,
    Plane,
    Users,
    Handshake,
    Bike,
} from "lucide-react";
import { INDUSTRIES } from "../data/industries";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/aa65bcde-0186-4814-9494-99862d62c7e5/images/49c433b19d5503ef4d04ef6c82ee6c3c399519258aa3e9f1ea8bf8fe508c7d84.png";

const PROBLEMS = [
    { icon: Cable, label: "Manual plug-in", desc: "Every charge cycle depends on a person to plug in and unplug." },
    { icon: ShieldAlert, label: "Exposed connectors", desc: "Live electrical contacts sit in daily contact with people and weather." },
    { icon: Wrench, label: "Regular maintenance", desc: "Cables, plugs and ports need routine inspection and replacement." },
    { icon: Activity, label: "Cable management", desc: "Trip hazards, tangled leads and blocked bays across every site." },
    { icon: Clock, label: "Downtime from damaged connectors", desc: "Damaged plugs pull vehicles and equipment out of service." },
    { icon: Droplets, label: "Harsh environments", desc: "Wet, dirty and industrial conditions accelerate connector wear." },
];

const SOLUTIONS = [
    { icon: Truck, title: "Commercial EVs", desc: "Vans, taxis and service vehicles that need to be ready every morning." },
    { icon: Users, title: "Fleet operators", desc: "Depot-wide charging that runs itself, without driver intervention." },
    { icon: Radio, title: "Industrial automation", desc: "Robots and autonomous systems that must charge without human help." },
    { icon: Warehouse, title: "Warehouses", desc: "AGVs, forklifts and mobile equipment topping up between tasks." },
    { icon: Bike, title: "Two-wheelers", desc: "Motorbikes, e-scooters and shared micromobility that park over a pad." },
    { icon: Building2, title: "Smart infrastructure", desc: "Charging integrated into parking, roads and public spaces." },
];

const VALUE_CARDS = [
    { icon: Sparkles, label: "Automatic", desc: "No manual intervention. Vehicles and machines charge on arrival." },
    { icon: CheckCircle2, label: "Reliable", desc: "Designed for daily commercial operation across shifts and seasons." },
    { icon: ShieldAlert, label: "Safer", desc: "No exposed contacts and no charging cables in the working area." },
    { icon: Wrench, label: "Lower maintenance", desc: "Fewer mechanical wear components. Sealed pads survive daily use." },
    { icon: Cpu, label: "Ready for automation", desc: "Ideal for autonomous vehicles, robotics and unmanned systems." },
    { icon: Layers3, label: "Clean infrastructure", desc: "Charging hardware integrates seamlessly into the environment." },
];

const PARTNER_TYPES = [
    { icon: Truck, label: "Automotive OEMs" },
    { icon: Users, label: "Fleet operators" },
    { icon: Building2, label: "Property developers" },
    { icon: Bike, label: "Micromobility operators" },
    { icon: Warehouse, label: "Warehouse automation" },
    { icon: Plane, label: "Airport operators" },
    { icon: Cpu, label: "Robotics manufacturers" },
    { icon: Zap, label: "Energy infrastructure" },
];

const Home = () => {
    return (
        <main data-testid="home-page" className="bg-white text-[#050B14]">
            {/* HERO */}
            <section
                data-testid="home-hero"
                className="relative min-h-[100vh] flex items-center bg-[#050B14] text-white overflow-hidden"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_IMG})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#050B14] via-[#050B14]/85 to-[#050B14]/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-transparent" />
                <div className="absolute inset-0 grid-bg opacity-50" />

                <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 py-32 lg:py-40 w-full">
                    <div className="max-w-3xl">
                        <div className="kicker text-[#00E5FF] mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-[#00E5FF]" />
                            Electric Wireless Solutions
                        </div>
                        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                            Wireless Charging.
                            <br />
                            <span className="text-gradient-cyan">Engineered</span> for
                            <br />
                            real-world applications.
                        </h1>
                        <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">
                            ELWIS develops intelligent wireless charging systems for
                            commercial vehicles, industrial equipment and smart
                            infrastructure. Our technology enables safe, reliable and
                            fully automated charging — without cables or exposed
                            connectors.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link
                                to="/industries"
                                data-testid="hero-explore-industries-link"
                                className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#050B14] px-6 py-3.5 rounded-md text-sm font-semibold hover:bg-white transition-colors"
                            >
                                Explore Solutions
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="hero-discuss-project-link"
                                className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-6 py-3.5 rounded-md text-sm font-semibold hover:bg-[#0044CC] transition-colors"
                            >
                                Discuss Your Project
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>

                        <div className="mt-20 grid grid-cols-3 gap-6 max-w-2xl">
                            {[
                                { v: "11 kW", l: "Current wireless AC / DC platform" },
                                { v: "Automatic", l: "No manual plug-in" },
                                { v: "Modular", l: "Ready to scale" },
                            ].map((s) => (
                                <div key={s.l} className="border-l border-white/15 pl-4">
                                    <div className="font-display font-bold text-2xl lg:text-3xl text-white">{s.v}</div>
                                    <div className="kicker text-white/50 mt-1">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* corner annotations */}
                <div className="absolute bottom-8 right-8 hidden lg:flex items-center gap-2 text-white/40 text-xs font-mono tracking-widest">
                    <span className="w-1.5 h-1.5 bg-[#00E5FF] rounded-full animate-pulse" />
                    WIRELESS LINK · READY
                </div>
            </section>

            {/* PROBLEM SECTION */}
            <section data-testid="home-problem" className="py-24 lg:py-32 bg-[#FAFAFA]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                        <div className="lg:col-span-5">
                            <div className="kicker text-[#0055FF] mb-5">01 / The Problem</div>
                            <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0F1C]">
                                Traditional charging was designed for people.
                            </h2>
                            <p className="mt-6 text-slate-600 leading-relaxed">
                                Plug-in charging still depends on a human to connect the
                                cable, inspect the port and manage the cycle. As
                                operations become more automated, that dependency
                                becomes the bottleneck.
                            </p>
                            <p className="mt-4 text-slate-600 leading-relaxed">
                                As automation grows, charging must evolve as well.
                            </p>
                        </div>

                        <div className="lg:col-span-7">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                                {PROBLEMS.map((p, idx) => (
                                    <div key={p.label} className="bg-white p-8 hover:bg-slate-50 transition-colors">
                                        <div className="flex items-center gap-3 mb-3">
                                            <span className="font-mono text-xs text-slate-400">P/0{idx + 1}</span>
                                            <p.icon className="w-5 h-5 text-[#0055FF]" />
                                        </div>
                                        <h3 className="font-display font-semibold text-lg text-[#0A0F1C]">{p.label}</h3>
                                        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SOLUTION SECTION */}
            <section data-testid="home-solution" className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
                        <div className="lg:col-span-7">
                            <div className="kicker text-[#0055FF] mb-5">02 / The ELWIS Solution</div>
                            <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0F1C]">
                                Charging that <em className="not-italic text-[#0055FF]">simply happens.</em>
                            </h2>
                        </div>
                        <div className="lg:col-span-5">
                            <p className="text-slate-600 leading-relaxed">
                                ELWIS eliminates the physical connector and enables
                                automatic energy transfer whenever a vehicle or machine
                                is correctly positioned.
                            </p>
                        </div>
                    </div>

                    <div className="mb-8 kicker text-slate-400">Designed for</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                        {SOLUTIONS.map((s, i) => (
                            <div key={s.title} className="bg-white p-8 group hover:bg-[#050B14] hover:text-white transition-all duration-300">
                                <div className="font-mono text-xs text-slate-400 group-hover:text-[#00E5FF]">S/0{i + 1}</div>
                                <s.icon className="w-7 h-7 mt-4 text-[#0055FF] group-hover:text-[#00E5FF] transition-colors" />
                                <h3 className="mt-5 font-display font-bold text-xl">{s.title}</h3>
                                <p className="mt-2 text-sm text-slate-600 group-hover:text-white/70 leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* INDUSTRY GRID */}
            <section data-testid="home-industries" className="py-24 lg:py-32 bg-[#FAFAFA]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
                        <div className="max-w-2xl">
                            <div className="kicker text-[#0055FF] mb-5">03 / Industries</div>
                            <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0F1C]">
                                Sectors where the 11 kW platform is already an excellent fit.
                            </h2>
                        </div>
                        <Link
                            to="/industries"
                            data-testid="industries-view-all-link"
                            className="inline-flex items-center gap-2 text-[#0055FF] font-semibold hover:gap-3 transition-all"
                        >
                            View all industries
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {INDUSTRIES.map((ind, i) => (
                            <Link
                                key={ind.slug}
                                to="/industries"
                                data-testid={`industry-card-${ind.slug}`}
                                className="relative group overflow-hidden bg-[#050B14] aspect-[4/3]"
                            >
                                <div
                                    className="absolute inset-0 bg-cover bg-center opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                                    style={{ backgroundImage: `url(${ind.image})` }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/60 to-transparent" />
                                <div className="relative h-full p-6 lg:p-7 flex flex-col justify-end text-white">
                                    <div className="kicker text-[#00E5FF] mb-3">0{i + 1}</div>
                                    <h3 className="font-display font-bold text-xl lg:text-2xl leading-tight">{ind.title}</h3>
                                    <p className="mt-2 text-sm text-white/70 line-clamp-2">{ind.short}</p>
                                    <div className="mt-4 flex items-center gap-2 text-[#00E5FF] text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                                        Explore use cases <ArrowUpRight className="w-4 h-4" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY WIRELESS CHARGING — DARK */}
            <section data-testid="home-advantages" className="py-24 lg:py-32 bg-[#050B14] text-white relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-40" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="max-w-3xl mb-16">
                        <div className="kicker text-[#00E5FF] mb-5">04 / Why wireless charging?</div>
                        <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                            The value isn't speed. It's <span className="text-gradient-cyan">everything else.</span>
                        </h2>
                        <p className="mt-6 text-white/60 leading-relaxed max-w-2xl">
                            For commercial and industrial operators, wireless charging
                            unlocks operational advantages that plug-in chargers simply
                            cannot deliver.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {VALUE_CARDS.map((v, idx) => (
                            <div key={v.label} className="bg-[#050B14] p-8 hover:bg-[#0A1220] transition-colors">
                                <div className="flex items-center justify-between mb-6">
                                    <span className="font-mono text-xs text-white/40">0{idx + 1} / 06</span>
                                    <v.icon className="w-5 h-5 text-[#00E5FF]" />
                                </div>
                                <h3 className="font-display font-bold text-2xl">{v.label}</h3>
                                <p className="mt-4 text-sm text-white/75 leading-relaxed">{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FUTURE VISION — DARK CINEMATIC */}
            <section data-testid="home-vision" className="py-32 lg:py-40 bg-[#0A1220] text-white relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-25"
                    style={{
                        backgroundImage: `url("https://images.unsplash.com/photo-1774031528911-608cd7a0cc70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGNpdHklMjBpbmZyYXN0cnVjdHVyZXxlbnwwfHx8fDE3Nzg1ODk0ODl8MA&ixlib=rb-4.1.0&q=85")`,
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A1220] via-[#0A1220]/85 to-transparent" />

                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="max-w-3xl">
                        <div className="kicker text-[#00E5FF] mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-[#00E5FF]" />
                            05 / Future Vision
                        </div>
                        <h2 className="font-display font-black text-5xl lg:text-6xl xl:text-7xl leading-[0.95] tracking-tight">
                            Building the infrastructure for
                            <br />
                            <span className="text-gradient-cyan">autonomous charging.</span>
                        </h2>
                        <p className="mt-10 text-lg text-white/70 max-w-2xl leading-relaxed">
                            The future is not about faster cables. It is about
                            eliminating cables altogether. ELWIS is developing the next
                            generation of charging infrastructure where vehicles,
                            machines and equipment receive energy automatically, safely
                            and intelligently.
                        </p>
                        <p className="mt-6 text-white/60 leading-relaxed max-w-2xl">
                            Today's platform delivers reliable wireless charging up to
                            11 kW for real-world commercial applications. As the
                            technology evolves, ELWIS will continue expanding the
                            capabilities of its platform while remaining focused on
                            reliability, automation and seamless integration.
                        </p>
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
                            {[
                                { i: Cpu, t: "Autonomous operation" },
                                { i: Layers3, t: "Seamless integration" },
                                { i: Radio, t: "Intelligent charging" },
                            ].map((v) => (
                                <div key={v.t} className="border border-white/15 bg-white/5 backdrop-blur-sm p-5">
                                    <v.i className="w-5 h-5 text-[#00E5FF]" />
                                    <div className="mt-3 font-display font-semibold">{v.t}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* PARTNERSHIPS */}
            <section data-testid="home-partnerships" className="py-24 lg:py-32 bg-[#FAFAFA]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
                        <div className="lg:col-span-7">
                            <div className="kicker text-[#0055FF] mb-5 flex items-center gap-3">
                                <Handshake className="w-4 h-4" />
                                06 / Partnerships
                            </div>
                            <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0F1C]">
                                Let's build the next generation of wireless charging infrastructure together.
                            </h2>
                        </div>
                        <div className="lg:col-span-5">
                            <p className="text-slate-600 leading-relaxed">
                                We collaborate with OEMs, operators, developers and
                                infrastructure partners to bring wireless charging into
                                real, day-to-day commercial deployments.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                        {PARTNER_TYPES.map((p) => (
                            <div
                                key={p.label}
                                data-testid={`partner-${p.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                                className="bg-white p-6 lg:p-8 flex flex-col gap-4 hover:bg-slate-50 transition-colors"
                            >
                                <p.icon className="w-6 h-6 text-[#0055FF]" />
                                <div className="font-display font-semibold text-base lg:text-lg text-[#0A0F1C] leading-snug">
                                    {p.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section data-testid="home-cta" className="py-24 lg:py-32 bg-white border-t border-slate-100">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="border border-slate-200 p-10 lg:p-16 bg-[#FAFAFA]">
                        <div className="grid lg:grid-cols-12 gap-10 items-center">
                            <div className="lg:col-span-8">
                                <div className="kicker text-[#0055FF] mb-4">Get in touch</div>
                                <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tight text-[#0A0F1C] leading-[1.05]">
                                    Ready to bring wireless charging into your operation?
                                </h2>
                                <p className="mt-6 text-slate-600 leading-relaxed max-w-2xl">
                                    Whether you operate a fleet, develop property or
                                    build automated systems — let's talk about what
                                    wireless charging could look like in your world.
                                </p>
                            </div>
                            <div className="lg:col-span-4 flex flex-col gap-3">
                                <Link
                                    to="/contact"
                                    data-testid="cta-schedule-consultation"
                                    className="inline-flex items-center justify-between bg-[#0055FF] text-white px-6 py-4 rounded-md font-semibold hover:bg-[#0044CC] transition-colors"
                                >
                                    Discuss Your Project
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                                <Link
                                    to="/contact"
                                    data-testid="cta-partnership-inquiry"
                                    className="inline-flex items-center justify-between bg-[#050B14] text-white px-6 py-4 rounded-md font-semibold hover:bg-[#111C30] transition-colors"
                                >
                                    Partnership Inquiry
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                                <Link
                                    to="/technology"
                                    data-testid="cta-technical-discussion"
                                    className="inline-flex items-center justify-between border border-slate-300 text-[#0A0F1C] px-6 py-4 rounded-md font-semibold hover:border-[#0055FF] hover:text-[#0055FF] transition-colors"
                                >
                                    Explore the Technology
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Home;
